export interface ParsedSvgData {
  viewBox: string;
  width: number;
  height: number;
  pathD: string;
}

export interface Matrix2D {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export const identityMatrix = (): Matrix2D => ({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 });

export const multiplyMatrix = (m1: Matrix2D, m2: Matrix2D): Matrix2D => ({
  a: m1.a * m2.a + m1.c * m2.b,
  b: m1.b * m2.a + m1.d * m2.b,
  c: m1.a * m2.c + m1.c * m2.d,
  d: m1.b * m2.c + m1.d * m2.d,
  e: m1.a * m2.e + m1.c * m2.f + m1.e,
  f: m1.b * m2.e + m1.d * m2.f + m1.f,
});

export const parseTransformAttribute = (transformStr: string | null): Matrix2D => {
  if (!transformStr) return identityMatrix();
  let current = identityMatrix();

  const regex = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]+)\)/gi;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(transformStr)) !== null) {
    const type = match[1].toLowerCase();
    const args = match[2].split(/[\s,]+/).map(Number).filter((n) => !isNaN(n));

    let m = identityMatrix();

    if (type === 'matrix' && args.length >= 6) {
      m = { a: args[0], b: args[1], c: args[2], d: args[3], e: args[4], f: args[5] };
    } else if (type === 'translate' && args.length >= 1) {
      m = { a: 1, b: 0, c: 0, d: 1, e: args[0], f: args[1] ?? 0 };
    } else if (type === 'scale' && args.length >= 1) {
      m = { a: args[0], b: 0, c: 0, d: args[1] ?? args[0], e: 0, f: 0 };
    } else if (type === 'rotate' && args.length >= 1) {
      const rad = (args[0] * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const cx = args[1] ?? 0;
      const cy = args[2] ?? 0;
      m = {
        a: cos,
        b: sin,
        c: -sin,
        d: cos,
        e: cx - cx * cos + cy * sin,
        f: cy - cx * sin - cy * cos,
      };
    }

    current = multiplyMatrix(current, m);
  }

  return current;
};

export const transformPoint = (x: number, y: number, m: Matrix2D): { x: number; y: number } => {
  return {
    x: Math.round((m.a * x + m.c * y + m.e) * 100) / 100,
    y: Math.round((m.b * x + m.d * y + m.f) * 100) / 100,
  };
};

export const getElementCombinedTransform = (el: Element): Matrix2D => {
  let current = identityMatrix();
  let node: Element | null = el;
  const nodes: Element[] = [];
  while (node && node.tagName.toLowerCase() !== 'svg') {
    nodes.unshift(node);
    node = node.parentElement;
  }
  nodes.forEach((n) => {
    const t = n.getAttribute('transform');
    if (t) {
      current = multiplyMatrix(current, parseTransformAttribute(t));
    }
  });
  return current;
};

/**
 * Parses SVG XML content string and extracts viewBox, width, height, and
 * a unified pathD string containing all visible geometries (<path>, <polygon>, <polyline>, <rect>, <circle>, <ellipse>).
 */
export const parseSvgContent = (text: string): ParsedSvgData => {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const svg = doc.querySelector('svg');
  if (!svg) {
    return {
      viewBox: '0 0 100 100',
      width: 100,
      height: 100,
      pathD: 'M 0 0 h 100 v 100 h -100 Z',
    };
  }

  // 1. Extract ViewBox & Dimensions
  let viewBoxAttr = svg.getAttribute('viewBox')?.trim();
  let widthAttr = svg.getAttribute('width')?.trim();
  let heightAttr = svg.getAttribute('height')?.trim();

  let vbWidth = 0;
  let vbHeight = 0;
  let hasValidViewBox = false;

  if (viewBoxAttr) {
    const vbParts = viewBoxAttr.split(/[\s,]+/).map(Number);
    if (vbParts.length >= 4 && !vbParts.some(isNaN) && vbParts[2] > 0 && vbParts[3] > 0) {
      vbWidth = vbParts[2];
      vbHeight = vbParts[3];
      hasValidViewBox = true;
    }
  }

  let width = parseFloat(widthAttr || '');
  let height = parseFloat(heightAttr || '');

  if (isNaN(width) || width <= 0) {
    width = hasValidViewBox ? vbWidth : 100;
  }
  if (isNaN(height) || height <= 0) {
    height = hasValidViewBox ? vbHeight : 100;
  }

  if (!hasValidViewBox) {
    viewBoxAttr = `0 0 ${width} ${height}`;
  }

  // 2. Extract Geometry Path
  const allElements = Array.from(
    svg.querySelectorAll('path, polygon, polyline, rect, circle, ellipse')
  );

  const visibleElements = allElements.filter((el) => {
    if (el.closest('defs, clipPath, mask, pattern')) return false;
    const style = el.getAttribute('style') || '';
    const display = el.getAttribute('display');
    const visibility = el.getAttribute('visibility');
    if (
      display === 'none' ||
      visibility === 'hidden' ||
      style.includes('display:none') ||
      style.includes('visibility:hidden')
    ) {
      return false;
    }
    return true;
  });

  const pathParts: string[] = [];

  visibleElements.forEach((el) => {
    const tagName = el.tagName.toLowerCase();
    const matrix = getElementCombinedTransform(el);

    if (tagName === 'path') {
      const d = el.getAttribute('d')?.trim();
      if (d) {
        pathParts.push(d);
      }
    } else if (tagName === 'polygon' || tagName === 'polyline') {
      const pointsStr = el.getAttribute('points')?.trim();
      if (pointsStr) {
        const nums = pointsStr.match(/-?\d+(?:\.\d+)?(?:e[-+]?\d+)?/gi)?.map(Number);
        if (nums && nums.length >= 4) {
          const pts: { x: number; y: number }[] = [];
          for (let i = 0; i < nums.length - 1; i += 2) {
            pts.push(transformPoint(nums[i], nums[i + 1], matrix));
          }
          let d = `M ${pts[0].x} ${pts[0].y}`;
          for (let i = 1; i < pts.length; i++) {
            d += ` L ${pts[i].x} ${pts[i].y}`;
          }
          if (tagName === 'polygon') {
            d += ' Z';
          }
          pathParts.push(d);
        }
      }
    } else if (tagName === 'rect') {
      const x = parseFloat(el.getAttribute('x') || '0') || 0;
      const y = parseFloat(el.getAttribute('y') || '0') || 0;
      const w = parseFloat(el.getAttribute('width') || '0') || 0;
      const h = parseFloat(el.getAttribute('height') || '0') || 0;

      if (w > 0 && h > 0) {
        // Transform the 4 corners of the rect
        const p0 = transformPoint(x, y, matrix);
        const p1 = transformPoint(x + w, y, matrix);
        const p2 = transformPoint(x + w, y + h, matrix);
        const p3 = transformPoint(x, y + h, matrix);

        const d = `M ${p0.x} ${p0.y} L ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p3.x} ${p3.y} Z`;
        pathParts.push(d);
      }
    } else if (tagName === 'circle') {
      const cx = parseFloat(el.getAttribute('cx') || '0') || 0;
      const cy = parseFloat(el.getAttribute('cy') || '0') || 0;
      const r = parseFloat(el.getAttribute('r') || '0') || 0;
      if (r > 0) {
        const center = transformPoint(cx, cy, matrix);
        const edge = transformPoint(cx + r, cy, matrix);
        const tr = Math.round(Math.hypot(edge.x - center.x, edge.y - center.y) * 100) / 100;
        const d = `M ${center.x - tr} ${center.y} a ${tr} ${tr} 0 1 0 ${2 * tr} 0 a ${tr} ${tr} 0 1 0 -${2 * tr} 0 Z`;
        pathParts.push(d);
      }
    } else if (tagName === 'ellipse') {
      const cx = parseFloat(el.getAttribute('cx') || '0') || 0;
      const cy = parseFloat(el.getAttribute('cy') || '0') || 0;
      const rx = parseFloat(el.getAttribute('rx') || '0') || 0;
      const ry = parseFloat(el.getAttribute('ry') || '0') || 0;
      if (rx > 0 && ry > 0) {
        const center = transformPoint(cx, cy, matrix);
        const edgeX = transformPoint(cx + rx, cy, matrix);
        const edgeY = transformPoint(cx, cy + ry, matrix);
        const trx = Math.round(Math.hypot(edgeX.x - center.x, edgeX.y - center.y) * 100) / 100;
        const tryVal = Math.round(Math.hypot(edgeY.x - center.x, edgeY.y - center.y) * 100) / 100;
        const d = `M ${center.x - trx} ${center.y} a ${trx} ${tryVal} 0 1 0 ${2 * trx} 0 a ${trx} ${tryVal} 0 1 0 -${2 * trx} 0 Z`;
        pathParts.push(d);
      }
    }
  });

  let pathD = pathParts.join(' ').trim();

  // Fallback if no valid geometry paths were found
  if (!pathD) {
    pathD = `M 0 0 h ${width} v ${height} h -${width} Z`;
  }

  return {
    viewBox: viewBoxAttr || `0 0 ${width} ${height}`,
    width,
    height,
    pathD,
  };
};
